/**
 * BJT IRT-inspired scoring
 *
 * 简化 IRT 能力估计模型：
 * - 每道题有难度参数 b 和区分度 a
 * - 答对高难度题时，权重系数递增（连续答对 bonus）
 * - 答错低难度题时，大幅扣除能力估值
 * - 最终将 theta 映射到 BJT 分段范围 200-700
 */

const DIFF_PARAMS = {
  'J1+': { b: 1.8, a: 1.5 }, // 极难、高区分度
  'J1':  { b: 0.9, a: 1.2 }, // 难
  'J2':  { b: 0.0, a: 1.0 }, // 中
  'J3':  { b: -1.5, a: 0.8 }, // 易
};

/** P(correct | theta, b, a) — 3PL 简化为 2PL */
function pCorrect(theta, b, a) {
  return 1 / (1 + Math.exp(-a * (theta - b)));
}

/**
 * 根据答题情况估算 BJT 分数（200–700）
 * @param {Array} questions  题目列表（含 difficulty、answer、id）
 * @param {Object} answers   { [questionId]: selectedIndex }
 * @returns {number} 估算分数
 */
export function estimateBJTScore(questions, answers) {
  const answered = questions.filter((q) => answers[q.id] !== undefined);
  if (answered.length === 0) return 400;

  let theta = 0;
  let hardStreak = 0; // 连续答对高难度题计数
  let weightMult = 1.0;

  answered.forEach((q) => {
    const params = DIFF_PARAMS[q.difficulty] || DIFF_PARAMS['J2'];
    const correct = answers[q.id] === q.answer;
    const isHard = q.difficulty === 'J1+' || q.difficulty === 'J1';
    const isEasy = q.difficulty === 'J3';

    if (correct) {
      if (isHard) {
        hardStreak++;
        weightMult = Math.min(1.7, 1.0 + hardStreak * 0.12);
      } else {
        hardStreak = 0;
        weightMult = 1.0;
      }
      theta += params.a * pCorrect(theta, params.b, params.a) * weightMult;
    } else {
      hardStreak = 0;
      weightMult = 1.0;
      if (isEasy) {
        // 答错简单题：大幅扣除
        theta -= params.a * 2.8;
      } else if (q.difficulty === 'J2') {
        theta -= params.a * 1.4;
      } else {
        // 答错难题，少量扣除
        theta -= params.a * 0.5;
      }
    }
  });

  // theta 通常在 -4 ~ +4，映射到 200-700
  const clamped = Math.max(-4, Math.min(4, theta));
  const score = Math.round(200 + ((clamped + 4) / 8) * 500);
  return Math.max(200, Math.min(700, score));
}

/** 根据分数返回能力段位标签 */
export function getAbilityLabel(score) {
  if (score >= 580) return 'A  优秀';
  if (score >= 490) return 'B+  良好';
  if (score >= 410) return 'B  一般';
  if (score >= 340) return 'C+  及格';
  if (score >= 270) return 'C  基础';
  return 'D  需加强';
}

/** 正态分布（用于绘制曲线）*/
export function gaussian(x, mean = 400, sigma = 85) {
  const z = (x - mean) / sigma;
  return Math.exp(-0.5 * z * z);
}
